import React, { useState, useEffect, useRef } from 'react';
import '../../../styles/Features.css';

const Features = () => {
  const [visibleFeatures, setVisibleFeatures] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const featuresRef = useRef(null);

  const features = [
    {
      id: 1,
      icon: 'fas fa-laptop-code',
      title: 'Interactive Learning',
      description: 'Engage with dynamic content, real-time exercises, and gamified experiences that adapt to your learning pace and age group.',
      animation: 'slide-in-left',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      hoverGradient: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      stats: { value: '95%', label: 'Engagement Rate' }
    },
    {
      id: 2,
      icon: 'fas fa-users',
      title: 'All Ages Welcome',
      description: 'From curious 10-year-olds to lifelong learners at 80, our programs are designed to be accessible and engaging for everyone.',
      animation: 'slide-in-right',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      hoverGradient: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
      stats: { value: '10-80', label: 'Age Range' }
    },
    {
      id: 3,
      icon: 'fas fa-user-graduate',
      title: 'Personal Dashboard',
      description: 'Track progress with large, clear interfaces and personalized recommendations that respect different learning speeds.',
      animation: 'slide-in-left',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      hoverGradient: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
      stats: { value: '24/7', label: 'Progress Tracking' }
    },
    {
      id: 4,
      icon: 'fas fa-chalkboard-teacher',
      title: 'Expert Instructors',
      description: 'Learn from experienced educators who understand how to teach complex concepts to diverse age groups effectively.',
      animation: 'slide-in-right',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      hoverGradient: 'linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)',
      stats: { value: '50+', label: 'Experts' }
    },
    {
      id: 5,
      icon: 'fas fa-video',
      title: 'Live Classes',
      description: 'Join interactive virtual classrooms with clear audio, large text, and intuitive controls designed for all technical skill levels.',
      animation: 'slide-in-left',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      hoverGradient: 'linear-gradient(135deg, #fee140 0%, #fa709a 100%)',
      stats: { value: '100+', label: 'Weekly Sessions' }
    },
    {
      id: 6,
      icon: 'fas fa-certificate',
      title: 'Recognized Certificates',
      description: 'Earn valuable certificates that showcase your new skills, whether you\'re starting a career or enriching your retirement.',
      animation: 'slide-in-right',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      hoverGradient: 'linear-gradient(135deg, #fed6e3 0%, #a8edea 100%)',
      stats: { value: '100%', label: 'Recognition' }
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.dataset.featureId;
            setVisibleFeatures(prev => [...prev, parseInt(id)]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const featureElements = document.querySelectorAll('.feature-card');
    featureElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="section features" id="about">
      <div className="container">
        {/* Centered Header */}
        <div className="features-header centered">
          <h2 className="section-title fade-in">Why Choose AMALIKA WORLD?</h2>
          <p className="section-subtitle fade-in">
            Comprehensive learning platform designed for learners from 10 to 80 years old
          </p>
        </div>
        
        {/* Enhanced Features Grid */}
        <div className="features-grid" ref={featuresRef}>
          {features.map((feature, index) => (
            <div 
              key={feature.id}
              data-feature-id={feature.id}
              className={`feature-card ${feature.animation} ${
                visibleFeatures.includes(feature.id) ? 'visible' : ''
              } ${activeCard === feature.id ? 'active' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onMouseEnter={() => setActiveCard(feature.id)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div className="feature-card-inner">
                {/* Card Background Glow */}
                <div 
                  className="card-glow"
                  style={{ background: feature.gradient }}
                ></div>
                
                {/* Main Card Content */}
                <div className="card-content">
                  {/* Icon Container with Animated Border */}
                  <div className="icon-container">
                    <div 
                      className="icon-background"
                      style={{ background: feature.gradient }}
                    ></div>
                    <div className="icon-border"></div>
                    <div className="feature-icon">
                      <i className={feature.icon}></i>
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="text-content">
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </div>

                  {/* Stats Badge */}
                  <div className="stats-badge">
                    <div className="stats-value">{feature.stats.value}</div>
                    <div className="stats-label">{feature.stats.label}</div>
                  </div>

                  {/* Hover Effect Layer */}
                  <div 
                    className="hover-effect"
                    style={{ background: feature.hoverGradient }}
                  ></div>

                  {/* Animated Border */}
                  <div className="animated-border"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Centered Stats Section - Three cards in front of each other */}
        <div className="features-stats centered">
          <div className="stats-grid organized-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fas fa-user-graduate"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">10K+</div>
                <div className="stat-label">Happy Learners</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fas fa-chalkboard-teacher"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">50+</div>
                <div className="stat-label">Expert Instructors</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fas fa-smile"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">100%</div>
                <div className="stat-label">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;